import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, FREE_FOLDER_LIMIT } from '../context/AuthContext';
import { useTeam } from '../context/TeamContext';
import { fetchPendingReportCount } from '../lib/supabase';
import { useTheme } from '../context/ThemeContext';
import { usePoints } from '../context/PointsContext';
import FolderManager from './FolderManager';

// ── Nav item ──────────────────────────────────────────────────
function NavItem({ icon, label, active, onClick, badge, indent, accent, dim }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: indent ? '6px 18px 6px 30px' : '7px 18px',
        borderRadius: 0,
        cursor: 'pointer',
        background: active ? 'var(--accent-dim)' : hov ? 'var(--surface2)' : 'transparent',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        transition: 'all 0.1s',
        userSelect: 'none',
      }}>
      {icon && (
        <span style={{
          fontSize: indent ? 11 : 14, flexShrink: 0, width: 18, textAlign: 'center',
          color: active ? 'var(--accent)' : dim ? 'var(--dim)' : 'var(--muted)',
        }}>{icon}</span>
      )}
      <span style={{
        fontSize: indent ? 12 : 13,
        color: active ? 'var(--accent)' : accent ? 'var(--accent)' : 'var(--text)',
        fontWeight: active ? 500 : 400,
        flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        lineHeight: 1.4,
      }}>{label}</span>
      {badge != null && badge > 0 && (
        <span style={{ fontSize: 9, padding: '2px 6px', background: 'var(--danger)', color: '#fff', borderRadius: 10, fontWeight: 600, flexShrink: 0 }}>{badge}</span>
      )}
    </div>
  );
}

// ── Section divider ───────────────────────────────────────────
function SectionDivider({ label }) {
  return (
    <div style={{ padding: '14px 18px 4px', fontSize: 10, letterSpacing: '0.08em', color: 'var(--dim)', textTransform: 'uppercase', fontWeight: 500 }}>
      {label}
    </div>
  );
}

// ── Folder row ────────────────────────────────────────────────
function FolderRow({ folder, active, onClick, draggingId, onDragOver, onDragLeave, onDrop, dragOver }) {
  return (
    <div
      onClick={onClick}
      onDragOver={draggingId ? e => { e.preventDefault(); onDragOver(folder.id); } : undefined}
      onDragLeave={draggingId ? () => onDragLeave() : undefined}
      onDrop={draggingId ? e => { e.preventDefault(); onDrop(draggingId, folder.id); } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: 9,
        padding: '6px 18px 6px 30px',
        cursor: 'pointer',
        background: active ? 'var(--accent-dim)' : dragOver ? `${folder.color}15` : 'transparent',
        borderLeft: active ? '2px solid var(--accent)' : '2px solid transparent',
        borderRadius: dragOver ? 'var(--radius-sm)' : 0,
        transition: 'all 0.1s',
      }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: folder.color, display: 'inline-block', flexShrink: 0 }} />
      <span style={{ fontSize: 12, color: active ? 'var(--accent)' : 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{folder.name}</span>
      {dragOver && <span style={{ fontSize: 10, color: folder.color }}>Drop</span>}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────
export default function Sidebar({ entries, activeFilter, onFilter, onClose, isOpen, folders, onFoldersChanged, draggingId, onDrop, onStartTour }) {
  const { user, profile, isAdmin, isPro, hasTeamsPlan, teamsPlan, signOut } = useAuth();
  const { balance, badge, loading: ptsLoading } = usePoints();
  const { teams, activeTeam, switchTeam, isTeamAdmin } = useTeam();
  const { isDark, toggle: toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [showFolderMgr, setShowFolderMgr] = useState(false);
  const [dragOverFolder, setDragOverFolder] = useState(null);
  const [reportBadge, setReportBadge] = useState(0);
  const [workspaceOpen, setWorkspaceOpen] = useState(true);
  const [teamOpen, setTeamOpen] = useState(true);

  useEffect(() => {
    if (isAdmin) fetchPendingReportCount().then(setReportBadge).catch(() => {});
  }, [isAdmin]);

  const isOn = (path) => location.pathname === path;
  const isOnStart = (path) => location.pathname.startsWith(path);

  const handleSignOut = async () => {
    await signOut();
    onClose();
    navigate('/app');
  };

  const go = (path) => { navigate(path); onClose(); };

  // Plan config
  let planLabel, planColor, planBg, planBorder, planTo;
  if (isAdmin) {
    planLabel = '✦ Admin'; planColor = '#ef9f27'; planBg = 'rgba(239,159,39,0.1)'; planBorder = 'rgba(239,159,39,0.3)'; planTo = '/settings';
  } else if (hasTeamsPlan && teamsPlan === 'agency') {
    planLabel = 'Agency'; planColor = '#ef9f27'; planBg = 'rgba(239,159,39,0.08)'; planBorder = 'rgba(239,159,39,0.25)'; planTo = '/settings?section=teams';
  } else if (hasTeamsPlan && teamsPlan === 'growth') {
    planLabel = 'Growth'; planColor = '#1d9e75'; planBg = 'rgba(29,158,117,0.08)'; planBorder = 'rgba(29,158,117,0.25)'; planTo = '/settings?section=teams';
  } else if (hasTeamsPlan && teamsPlan === 'starter') {
    planLabel = 'Starter'; planColor = '#7f77dd'; planBg = 'rgba(127,119,221,0.08)'; planBorder = 'rgba(127,119,221,0.25)'; planTo = '/settings?section=teams';
  } else if (isPro) {
    planLabel = 'Pro'; planColor = '#7f77dd'; planBg = 'rgba(127,119,221,0.08)'; planBorder = 'rgba(127,119,221,0.25)'; planTo = '/settings?section=plan';
  } else {
    planLabel = 'Free'; planColor = 'var(--dim)'; planBg = 'transparent'; planBorder = 'var(--border)'; planTo = '/premium';
  }

  const hasTeam = (hasTeamsPlan || isAdmin) && teams.length > 0;
  const urlParams = new URLSearchParams(location.search);
  const isSkillsHome = isOn('/skills') && !urlParams.get('folder') && urlParams.get('tab') !== 'playbooks';
  const isWorkflows  = urlParams.get('tab') === 'playbooks' && isOn('/skills') && !urlParams.get('folder');
  const isInFolder   = !!urlParams.get('folder') && isOn('/skills');

  return (
    <>
      <div className={`sidebar-overlay ${isOpen ? 'show' : ''}`} onClick={onClose} />
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>

        {/* ── Logo ── */}
        <Link to="/" onClick={onClose} className="sidebar-logo" style={{ textDecoration: 'none' }}>
          CopyPaste<span>Genius</span>
        </Link>

        {/* ── Compact points bar — always visible when signed in ── */}
        {user && (
          <div style={{
            margin: '6px 12px 2px',
            padding: '7px 11px',
            borderRadius: 9,
            background: 'var(--surface2)',
            border: '0.5px solid var(--border)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
          }}>
            {/* Left: username + settings gear */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {profile?.display_name || profile?.username || user.email}
              </span>
              <Link to="/settings" onClick={onClose} title="Settings"
                style={{ textDecoration: 'none', color: 'var(--dim)', fontSize: 15, flexShrink: 0, lineHeight: 1 }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--text)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--dim)'}>
                ⚙
              </Link>
            </div>

            {/* Right: points + plan */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0 }}>
              <span
                title={`${balance} points${badge?.tier ? ' · ' + badge.tier : ''}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 10.5, fontWeight: 600, padding: '3px 8px', borderRadius: 20,
                  background: 'rgba(127,119,221,0.12)', border: '0.5px solid rgba(127,119,221,0.35)',
                  color: '#7f77dd', cursor: 'default', letterSpacing: '-0.01em', whiteSpace: 'nowrap',
                }}>
                <span style={{ fontSize: 9 }}>✦</span>
                {ptsLoading ? '…' : `${balance.toLocaleString()} pts`}
                {badge?.emoji && badge.emoji !== '—' && <span style={{ marginLeft: 1 }}>{badge.emoji}</span>}
              </span>

              <Link to={planTo} onClick={onClose} style={{ textDecoration: 'none' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: 3,
                  fontSize: 10.5, fontWeight: 500, padding: '3px 8px', borderRadius: 20,
                  background: planBg, border: `0.5px solid ${planBorder}`,
                  color: planColor, cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                  <span style={{ width: 4, height: 4, borderRadius: '50%', background: planColor, flexShrink: 0, boxShadow: isPro || hasTeamsPlan || isAdmin ? `0 0 4px ${planColor}` : 'none' }} />
                  {planLabel}
                </span>
              </Link>
            </div>
          </div>
        )}

        {/* ── Signed-out CTA ── */}
        {!user && (
          <div style={{ margin: '8px 12px 4px' }}>
            <Link to="/auth" onClick={onClose} style={{
              display: 'block', padding: '8px 12px', textAlign: 'center',
              background: 'var(--accent-dim)', border: '0.5px solid var(--accent-border)',
              borderRadius: 8, color: 'var(--accent)', fontSize: 12, textDecoration: 'none',
            }}>Sign in / Sign up →</Link>
          </div>
        )}

        {/* ── Nav ── */}
        <nav style={{ flex: 1, overflowY: 'auto', paddingBottom: 8 }}>

          {/* 1. Home */}
          <NavItem
            icon="🏠"
            label="Home"
            active={isOn('/app') && !urlParams.get('folder')}
            onClick={() => { onFilter('all'); go('/app'); }}
          />

          {/* 2. The Library */}
          <NavItem
            icon="📚"
            label="The Library"
            active={isOnStart('/marketplace') || isOn('/creator')}
            onClick={() => go('/marketplace')}
          />
          {user && (isOnStart('/marketplace') || isOn('/creator')) && (
            <NavItem
              icon="⭐"
              label="Creator Dashboard"
              indent
              active={isOn('/creator')}
              onClick={() => go('/creator')}
            />
          )}

          {/* 3. My Workspace — only signed in */}
          {user && (
            <>
              <div
                onClick={() => setWorkspaceOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 18px', cursor: 'pointer', userSelect: 'none',
                  borderLeft: (isOn('/skills') || isInFolder) ? '2px solid var(--accent)' : '2px solid transparent',
                  background: (isOn('/skills') || isInFolder) ? 'var(--accent-dim)' : 'transparent',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ fontSize: 14, flexShrink: 0, width: 18, textAlign: 'center', color: (isOn('/skills') || isInFolder) ? 'var(--accent)' : 'var(--muted)' }}>⚡</span>
                  <span style={{ fontSize: 13, color: (isOn('/skills') || isInFolder) ? 'var(--accent)' : 'var(--text)', fontWeight: (isOn('/skills') || isInFolder) ? 500 : 400 }}>My Workspace</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--dim)', transform: workspaceOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▾</span>
              </div>

              {workspaceOpen && (
                <>
                  {/* Skills */}
                  <NavItem icon="⚡" label="My Skills"    indent active={isSkillsHome} onClick={() => go('/skills')} />
                  <NavItem icon="📦" label="My Workflows" indent active={isWorkflows}  onClick={() => go('/skills?tab=playbooks')} />

                  {/* Folders */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 18px 2px 30px' }}>
                    <span style={{ fontSize: 10, color: 'var(--dim)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>
                      Folders
                      {!isPro && !isAdmin && (folders || []).length >= FREE_FOLDER_LIMIT && (
                        <span style={{ marginLeft: 4, fontSize: 9 }}>🔒</span>
                      )}
                    </span>
                    {(isPro || isAdmin || (folders || []).length < FREE_FOLDER_LIMIT) && (
                      <button onClick={e => { e.stopPropagation(); setShowFolderMgr(true); }} style={{
                        background: 'none', border: 'none', color: 'var(--dim)', fontSize: 15,
                        cursor: 'pointer', lineHeight: 1, padding: '0 2px',
                      }} title="Manage folders">+</button>
                    )}
                  </div>

                  {!isPro && !isAdmin && (folders || []).length >= FREE_FOLDER_LIMIT && (
                    <div style={{ padding: '4px 30px 6px', fontSize: 11, color: 'var(--dim)', lineHeight: 1.5 }}>
                      <a href="/premium" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Upgrade</a> for unlimited folders
                    </div>
                  )}

                  {(folders || []).map(f => (
                    <FolderRow
                      key={f.id}
                      folder={f}
                      active={isInFolder && urlParams.get('folder') === f.id}
                      onClick={() => { onClose(); navigate(`/skills?folder=${f.id}`); }}
                      draggingId={draggingId}
                      onDragOver={setDragOverFolder}
                      onDragLeave={() => setDragOverFolder(null)}
                      onDrop={(dragId, folderId) => { onDrop && onDrop(dragId, folderId); setDragOverFolder(null); }}
                      dragOver={dragOverFolder === f.id}
                    />
                  ))}

                  {(folders || []).length === 0 && (
                    <div style={{ padding: '2px 30px 8px', fontSize: 11, color: 'var(--dim)' }}>
                      No folders yet — tap + to create
                    </div>
                  )}

                  {!isPro && !isAdmin && (
                    <div style={{ padding: '6px 18px 4px', fontSize: 11, color: 'var(--dim)', lineHeight: 1.5 }}>
                      <a href="/premium" style={{ color: 'var(--accent)', textDecoration: 'none' }}>Upgrade to Pro</a> to share &amp; make skills private
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* 4. Team */}
          {user && (hasTeamsPlan || isAdmin || teams.length > 0) && (
            <>
              <div
                onClick={() => setTeamOpen(o => !o)}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '7px 18px', cursor: 'pointer', userSelect: 'none',
                  borderLeft: isOnStart('/team') ? '2px solid var(--accent)' : '2px solid transparent',
                  background: isOnStart('/team') ? 'var(--accent-dim)' : 'transparent',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{ fontSize: 14, flexShrink: 0, width: 18, textAlign: 'center', color: isOnStart('/team') ? 'var(--accent)' : 'var(--muted)' }}>👥</span>
                  <span style={{ fontSize: 13, color: isOnStart('/team') ? 'var(--accent)' : 'var(--text)', fontWeight: isOnStart('/team') ? 500 : 400 }}>Team</span>
                </div>
                <span style={{ fontSize: 11, color: 'var(--dim)', transform: teamOpen ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.15s', display: 'inline-block' }}>▾</span>
              </div>

              {teamOpen && (
                <>
                  {!hasTeamsPlan && !isAdmin && (
                    <NavItem icon="⚡" label="Upgrade to create teams" indent accent onClick={() => go('/premium')} />
                  )}
                  {(hasTeamsPlan || isAdmin) && teams.map(t => {
                    const isActive = activeTeam?.id === t.id;
                    const isOwnerOrAdmin = t.my_role === 'owner' || t.my_role === 'admin';
                    return (
                      <div key={t.id}>
                        <div
                          onClick={() => { switchTeam(t.id); navigate('/team'); }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 9,
                            padding: '6px 18px 6px 30px', cursor: 'pointer',
                            background: isActive && isOnStart('/team') ? 'var(--accent-dim)' : 'transparent',
                            borderLeft: isActive && isOnStart('/team') ? '2px solid var(--accent)' : '2px solid transparent',
                            transition: 'all 0.1s',
                          }}>
                          <span style={{ fontSize: 11, color: 'var(--dim)', width: 18, textAlign: 'center' }}>🏢</span>
                          <span style={{ fontSize: 12, color: 'var(--text)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                          {isActive && (
                            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 6, background: 'var(--accent-dim)', color: 'var(--accent)', border: '0.5px solid var(--accent-border)', flexShrink: 0 }}>active</span>
                          )}
                        </div>
                        {isActive && (
                          <>
                            <NavItem icon="📋" label="Dashboard" indent active={isOn('/team') && !isOn('/team/settings')} onClick={() => go('/team')} />
                            {isOwnerOrAdmin && (
                              <NavItem icon="⚙" label="Settings" indent active={isOn('/team/settings')} onClick={() => go('/team/settings')} />
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                  {(isPro || isAdmin) && (
                    <NavItem icon="+" label="Create a team" indent accent onClick={() => go('/team/create')} />
                  )}
                </>
              )}
            </>
          )}

          {/* 5. Admin */}
          {isAdmin && (
            <>
              <div style={{ height: '0.5px', background: 'var(--border)', margin: '8px 18px' }} />
              <NavItem icon="🛡" label="Admin panel"       active={isOn('/admin')}            badge={reportBadge} onClick={() => go('/admin')} />
              <NavItem icon="✦"  label="Curate the Library" active={isOn('/admin/marketplace')} onClick={() => go('/admin/marketplace')} />
            </>
          )}

        </nav>

        {/* ── Footer ── */}
        <div className="sidebar-footer">
          {/* Sign out — only shown when signed in */}
          {user && (
            <button onClick={handleSignOut} style={{
              width: '100%', background: 'none',
              border: '0.5px solid var(--border2)', borderRadius: 6,
              padding: '5px 0', color: 'var(--dim)', fontFamily: 'var(--font)',
              fontSize: 11, cursor: 'pointer', marginBottom: 8,
            }}>Sign out</button>
          )}

          {/* Theme + tour + links */}
          <div className="theme-toggle" onClick={toggleTheme}>
            <span className="theme-toggle-label">{isDark ? '🌙 Dark mode' : '☀️ Light mode'}</span>
            <div className={`theme-switch ${isDark ? 'on' : ''}`}>
              <div className="theme-switch-thumb" />
            </div>
          </div>
          <button
            onClick={() => { onStartTour && onStartTour(); onClose(); }}
            style={{
              background: 'none', border: '0.5px solid var(--border2)', borderRadius: 6,
              padding: '5px 10px', width: '100%', fontFamily: 'var(--font)',
              fontSize: 11.5, color: 'var(--muted)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 6,
              marginBottom: 6, transition: 'border-color 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-border)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border2)'}
          >
            <span>✦</span> Take the tour
          </button>
          <div style={{ marginTop: 2, fontSize: 11, color: 'var(--dim)' }}>CopyPasteGenius v0.2</div>
          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: '3px 10px' }}>
            <Link to="/legal/terms"   onClick={onClose} style={{ fontSize: 10, color: 'var(--dim)', textDecoration: 'none' }}>Terms</Link>
            <Link to="/legal/privacy" onClick={onClose} style={{ fontSize: 10, color: 'var(--dim)', textDecoration: 'none' }}>Privacy</Link>
            <Link to="/legal/cookies" onClick={onClose} style={{ fontSize: 10, color: 'var(--dim)', textDecoration: 'none' }}>Cookies</Link>
            <Link to="/legal/refunds" onClick={onClose} style={{ fontSize: 10, color: 'var(--dim)', textDecoration: 'none' }}>Refunds</Link>
          </div>
        </div>

      </aside>

      {showFolderMgr && (
        <FolderManager
          folders={folders || []}
          onChanged={(updated) => { onFoldersChanged(updated); }}
          onClose={() => setShowFolderMgr(false)}
        />
      )}
    </>
  );
}